import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { NavBar } from '../components/NavBar';
import { Utensils, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { 
  TodaysMealsView, 
  AllPlansView, 
  PlanDetailModal 
} from '../components/meal-plan';

export const MealPlanPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState(null);
  const [todaysMeals, setTodaysMeals] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('today');
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recRes, todayRes, plansRes] = await Promise.all([
        api().get('/meal-plans/recommended'),
        api().get('/meal-plans/my-plan/today'),
        api().get('/meal-plans')
      ]);
      
      setRecommendations(recRes.data);
      setTodaysMeals(todayRes.data);
      setAllPlans(plansRes.data.meal_plans || []);
    } catch (err) {
      toast.error('Failed to load meal plans');
    }
    setLoading(false);
  };

  const seedMealPlans = async () => {
    setSeeding(true);
    try {
      await api().post('/meal-plans/seed');
      toast.success('Meal plans created!');
      fetchData();
    } catch (err) {
      toast.error('Failed to seed meal plans');
    }
    setSeeding(false);
  };

  const followPlan = async (planId) => {
    try {
      await api().post(`/meal-plans/${planId}/follow`);
      toast.success('Started following meal plan!');
      fetchData();
      setActiveTab('today');
    } catch (err) {
      toast.error('Failed to follow plan');
    }
  };

  const advanceDay = async () => {
    try {
      const res = await api().post('/meal-plans/advance-day');
      if (res.data.completed) {
        toast.success('Congratulations! You completed the meal plan!');
      } else {
        toast.success(`Advanced to Day ${res.data.current_day}`);
      }
      fetchData();
    } catch (err) {
      toast.error('Failed to advance day');
    }
  };

  const viewPlanDetail = async (planId) => {
    try {
      const res = await api().get(`/meal-plans/${planId}`);
      setSelectedPlan(res.data);
    } catch (err) {
      toast.error('Failed to load plan details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="meal-plan-page">
      <NavBar title="Meal Plans" />

      <main className="container mx-auto px-6 py-8">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">
                <Utensils className="h-8 w-8 text-primary inline mr-3" />
                Personalized <span className="text-primary">Nutrition</span>
              </h2>
              <p className="text-muted-foreground">Meal plans tailored to your fitness goals</p>
            </div>
            
            {/* Tab Switcher */}
            <div className="flex gap-2 bg-card border border-border p-1">
              <Button
                variant={activeTab === 'today' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('today')}
                className={activeTab === 'today' ? 'bg-primary text-black' : ''}
                data-testid="today-tab"
              >
                Today's Meals
              </Button>
              <Button
                variant={activeTab === 'plans' ? 'default' : 'ghost'}
                onClick={() => setActiveTab('plans')}
                className={activeTab === 'plans' ? 'bg-primary text-black' : ''}
                data-testid="plans-tab"
              >
                All Plans
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Today's Meals Tab */}
        {activeTab === 'today' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <TodaysMealsView
              todaysMeals={todaysMeals}
              recommendations={recommendations}
              advanceDay={advanceDay}
              setActiveTab={setActiveTab}
              allPlans={allPlans}
              seedMealPlans={seedMealPlans}
              seeding={seeding}
            />
          </motion.div>
        )}

        {/* All Plans Tab */}
        {activeTab === 'plans' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <AllPlansView
              allPlans={allPlans}
              recommendations={recommendations}
              followPlan={followPlan}
              viewPlanDetail={viewPlanDetail}
              seedMealPlans={seedMealPlans}
              seeding={seeding}
            />
          </motion.div>
        )}

        {/* Plan Detail Modal */}
        <PlanDetailModal
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          followPlan={followPlan}
        />
      </main>
    </div>
  );
};
